import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { api } from '../services/api'

interface CustomerMapping {
  integrationId: string
  externalId: string
  externalName?: string
  syncStatus: string
}

interface SetupCustomer {
  id: string
  name: string
  domain?: string
  status: string
  totalDevices: number
  totalLicenses: number
  totalUsers: number
  monthlyRevenue: number
  hasAzureSubscription: boolean
  mappings: CustomerMapping[]
}

interface ExternalCustomer {
  externalId: string
  name: string
  domain?: string
  isMapped: boolean
}

interface SyncJob {
  id: number
  status: string
  totalCustomers: number
  processedCustomers: number
  successCount: number
  failedCount: number
}

export default function SetupCustomers() {
  const [customers, setCustomers] = useState<SetupCustomer[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [externalCustomers, setExternalCustomers] = useState<ExternalCustomer[]>([])
  const [selectedImports, setSelectedImports] = useState<string[]>([])
  const [importing, setImporting] = useState(false)
  const [, setImportSearch] = useState('')
  const [newCustomer, setNewCustomer] = useState({ name: '', domain: '', status: 'Active' })
  const [creating, setCreating] = useState(false)
  const [searchAvailable, setSearchAvailable] = useState('')
  const [searchImported, setSearchImported] = useState('')
  const [syncing, setSyncing] = useState(false)
  const [syncJob, setSyncJob] = useState<SyncJob | null>(null)
  const [syncingPartnerCenter, setSyncingPartnerCenter] = useState(false)
  const { getAccessToken } = useAuth()

  const fetchCustomers = async () => {
    try {
      setLoading(true)
      setError(null)
      const token = await getAccessToken()
      const data = await api.getSetupCustomers(token)
      setCustomers(data.customers || [])
    } catch (err) {
      console.error('Failed to fetch customers:', err)
      setError('Failed to load customers. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCustomers()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSyncLicenses = async () => {
    try {
      setSyncing(true)
      setSyncJob(null)
      setError(null)
      const token = await getAccessToken()

      // Start the sync
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/setup/customers/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Sync failed')
      }

      // Poll for sync job status
      if (data.syncJobId) {
        pollSyncStatus(data.syncJobId, token)
      } else {
        setSyncing(false)
        await fetchCustomers()
      }
    } catch (err) {
      console.error('Failed to sync licenses:', err)
      setError(err instanceof Error ? err.message : 'Failed to sync licenses')
      setSyncing(false)
    }
  }

  const pollSyncStatus = async (syncJobId: number, token: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/setup/customers/sync/${syncJobId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to get sync status')
      }

      const job: SyncJob = await response.json()
      setSyncJob(job)

      // Keep polling if still running
      if (job.status === 'Running') {
        setTimeout(() => pollSyncStatus(syncJobId, token), 2000)
      } else {
        // Sync complete, refresh customers and stop syncing
        setSyncing(false)
        await fetchCustomers()
      }
    } catch (err) {
      console.error('Failed to poll sync status:', err)
      setSyncing(false)
    }
  }

  const handleSyncPartnerCenter = async () => {
    try {
      setSyncingPartnerCenter(true)
      setError(null)
      const token = await getAccessToken()

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/sync-partner-center`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || data.details || 'Sync failed')
      }

      // After sync completes, open the import modal
      const customersData = await api.getExternalCustomers(token, 'partner-center')
      setExternalCustomers(customersData.customers || [])
      setSelectedImports([])
      setSearchAvailable('')
      setSearchImported('')
      setImportSearch('')
      setShowImportModal(true)
    } catch (err) {
      console.error('Failed to sync Partner Center:', err)
      setError(err instanceof Error ? err.message : 'Failed to sync Partner Center data.')
    } finally {
      setSyncingPartnerCenter(false)
    }
  }

  // Filter functions for import modal
  const availableCustomers = externalCustomers
    .filter((c) => !c.isMapped)
    .filter((c) =>
      c.name.toLowerCase().includes(searchAvailable.toLowerCase()) ||
      (c.domain?.toLowerCase().includes(searchAvailable.toLowerCase()) ?? false)
    )

  const importedCustomers = externalCustomers
    .filter((c) => c.isMapped)
    .filter((c) =>
      c.name.toLowerCase().includes(searchImported.toLowerCase()) ||
      (c.domain?.toLowerCase().includes(searchImported.toLowerCase()) ?? false)
    )

  const handleImport = async () => {
    if (selectedImports.length === 0) return

    try {
      setImporting(true)
      const token = await getAccessToken()
      await api.importCustomers(token, {
        integrationId: 'partner-center',
        externalIds: selectedImports,
      })
      setShowImportModal(false)
      await fetchCustomers()

      // Automatically sync licenses for the newly imported customers
      handleSyncLicenses()
    } catch (err) {
      console.error('Failed to import customers:', err)
      setError('Failed to import customers.')
    } finally {
      setImporting(false)
    }
  }

  const handleCreateCustomer = async () => {
    if (!newCustomer.name.trim()) return

    try {
      setCreating(true)
      const token = await getAccessToken()
      await api.createSetupCustomer(token, newCustomer)
      setShowAddModal(false)
      setNewCustomer({ name: '', domain: '', status: 'Active' })
      await fetchCustomers()
    } catch (err) {
      console.error('Failed to create customer:', err)
      setError('Failed to create customer.')
    } finally {
      setCreating(false)
    }
  }

  const getIntegrationBadges = (mappings: CustomerMapping[]) => {
    const integrationColors: Record<string, string> = {
      'partner-center': 'bg-blue-100 text-blue-800',
      'ninjaone': 'bg-green-100 text-green-800',
      'huntress': 'bg-purple-100 text-purple-800',
      'quickbooks': 'bg-yellow-100 text-yellow-800',
      'screenconnect': 'bg-gray-100 text-gray-800',
    }

    const integrationNames: Record<string, string> = {
      'partner-center': 'MS',
      'ninjaone': 'Ninja',
      'huntress': 'Hunt',
      'quickbooks': 'QB',
      'screenconnect': 'SC',
    }

    return mappings.map((m) => (
      <span
        key={m.integrationId}
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mr-1 ${
          integrationColors[m.integrationId] || 'bg-gray-100 text-gray-800'
        }`}
        title={m.externalName || m.externalId}
      >
        {integrationNames[m.integrationId] || m.integrationId}
      </span>
    ))
  }

  if (loading) {
    return (
      <div className="px-4 sm:px-0">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <p className="mt-2 text-sm text-gray-500">Loading customers...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-0">
      {/* Sub-navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <Link
            to="/setup/customers"
            className="border-primary-500 text-primary-600 whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium"
          >
            Customers
          </Link>
          <Link
            to="/setup/integrations"
            className="border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium"
          >
            Integrations
          </Link>
        </nav>
      </div>

      {/* Header */}
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h2 className="text-2xl font-bold text-gray-900">Manage Customers</h2>
          <p className="mt-2 text-sm text-gray-700">
            Add customers to your Customer 360 environment and link them to external systems.
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none space-x-2">
          <button
            onClick={handleSyncLicenses}
            disabled={syncing || customers.length === 0}
            className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {syncing ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Syncing...
              </>
            ) : (
              <>
                <svg className="-ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
                Sync Licenses
              </>
            )}
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            + Add Customer
          </button>
          <button
            onClick={handleSyncPartnerCenter}
            disabled={syncingPartnerCenter}
            className="inline-flex items-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {syncingPartnerCenter ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Syncing...
              </>
            ) : (
              'Sync from Partner Center'
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Sync Progress Bar */}
      {syncing && syncJob && (
        <div className="mb-4 rounded-lg bg-blue-50 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
              <svg className="animate-spin h-5 w-5 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-sm font-medium text-blue-800">
                Syncing license data...
              </span>
            </div>
            <span className="text-sm text-blue-600">
              {syncJob.processedCustomers} / {syncJob.totalCustomers} customers
            </span>
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2.5">
            <div
              className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${syncJob.totalCustomers > 0 ? (syncJob.processedCustomers / syncJob.totalCustomers) * 100 : 0}%` }}
            ></div>
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-blue-600">
            <span>{syncJob.successCount} successful</span>
            {syncJob.failedCount > 0 && (
              <span className="text-red-600">{syncJob.failedCount} failed</span>
            )}
          </div>
        </div>
      )}

      {/* Sync Completed Message */}
      {!syncing && syncJob && syncJob.status !== 'Running' && (
        <div className={`mb-4 rounded-lg p-4 ${syncJob.failedCount === 0 ? 'bg-green-50' : 'bg-yellow-50'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {syncJob.failedCount === 0 ? (
                <svg className="h-5 w-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              )}
              <span className={`text-sm font-medium ${syncJob.failedCount === 0 ? 'text-green-800' : 'text-yellow-800'}`}>
                Sync completed: {syncJob.successCount} successful
                {syncJob.failedCount > 0 && `, ${syncJob.failedCount} failed`}
              </span>
            </div>
            <button
              onClick={() => setSyncJob(null)}
              className="text-gray-400 hover:text-gray-500"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Customer Table */}
      {customers.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900">No customers yet</h3>
          <p className="mt-2 text-sm text-gray-500">
            Get started by syncing customers from Partner Center or adding them manually.
          </p>
          <div className="mt-6 space-x-2">
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              + Add Customer
            </button>
            <button
              onClick={handleSyncPartnerCenter}
              disabled={syncingPartnerCenter}
              className="inline-flex items-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {syncingPartnerCenter ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Syncing...
                </>
              ) : (
                'Sync from Partner Center'
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                  Customer
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Status
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Integrations
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Licenses
                </th>
                <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                  Azure
                </th>
                <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {customers.map((customer) => (
                <tr key={customer.id}>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 sm:pl-6">
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900">{customer.name}</span>
                      {customer.domain && (
                        <span className="text-sm text-gray-500">{customer.domain}</span>
                      )}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <span
                      className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                        customer.status === 'Active'
                          ? 'bg-green-100 text-green-800'
                          : customer.status === 'Inactive'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {customer.status}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    {customer.mappings.length > 0 ? (
                      getIntegrationBadges(customer.mappings)
                    ) : (
                      <span className="text-gray-400">None linked</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {customer.totalLicenses}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    {customer.hasAzureSubscription ? (
                      <span className="inline-flex items-center rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-800">
                        <svg className="mr-1 h-3 w-3" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12.042 2.88l7.68 4.8c.14.08.23.22.23.38v7.86c0 .16-.09.3-.23.38l-7.68 4.8c-.14.08-.31.08-.45 0l-7.68-4.8a.44.44 0 0 1-.23-.38V8.06c0-.16.09-.3.23-.38l7.68-4.8c.14-.08.31-.08.45 0z"/>
                        </svg>
                        Yes
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                    <Link
                      to={`/setup/customers/${customer.id}`}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      Configure
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Import Modal - Two Pane Layout */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowImportModal(false)} />
            <div className="relative transform overflow-hidden rounded-lg bg-white shadow-xl sm:my-8 w-full max-w-5xl">
              {/* Header */}
              <div className="border-b border-gray-200 px-6 py-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Import from Partner Center</h3>
                  <button
                    onClick={() => setShowImportModal(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Two Pane Content */}
              <div className="grid grid-cols-2 divide-x divide-gray-200">
                {/* Left Pane - Available to Import */}
                <div className="p-4">
                  <div className="mb-3">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">
                      Available to Import ({externalCustomers.filter((c) => !c.isMapped).length})
                    </h4>
                    <input
                      type="text"
                      placeholder="Search customers..."
                      value={searchAvailable}
                      onChange={(e) => setSearchAvailable(e.target.value)}
                      className="w-full rounded-md border border-gray-300 bg-white text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-2.5 px-3 text-base"
                    />
                  </div>
                  <div className="h-80 overflow-y-auto border border-gray-200 rounded-md">
                    {availableCustomers.length === 0 ? (
                      <div className="p-4 text-center text-sm text-gray-500">
                        {searchAvailable ? 'No customers match your search.' : 'All customers have been imported.'}
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {availableCustomers.map((customer) => (
                          <label
                            key={customer.externalId}
                            className="flex items-center p-3 hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedImports.includes(customer.externalId)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedImports([...selectedImports, customer.externalId])
                                } else {
                                  setSelectedImports(selectedImports.filter((id) => id !== customer.externalId))
                                }
                              }}
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                            />
                            <div className="ml-3 min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-900 truncate">{customer.name}</p>
                              {customer.domain && (
                                <p className="text-xs text-gray-500 truncate">{customer.domain}</p>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                  {availableCustomers.length > 0 && (
                    <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                      <span>{selectedImports.length} selected</span>
                      <button
                        onClick={() => setSelectedImports(availableCustomers.map((c) => c.externalId))}
                        className="text-primary-600 hover:text-primary-500"
                      >
                        Select all ({availableCustomers.length})
                      </button>
                    </div>
                  )}
                </div>

                {/* Right Pane - Already Imported */}
                <div className="p-4 bg-gray-50">
                  <div className="mb-3">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">
                      Already Imported ({externalCustomers.filter((c) => c.isMapped).length})
                    </h4>
                    <input
                      type="text"
                      placeholder="Search imported..."
                      value={searchImported}
                      onChange={(e) => setSearchImported(e.target.value)}
                      className="w-full rounded-md border border-gray-300 bg-white text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-2.5 px-3 text-base"
                    />
                  </div>
                  <div className="h-80 overflow-y-auto border border-gray-200 rounded-md bg-white">
                    {importedCustomers.length === 0 ? (
                      <div className="p-4 text-center text-sm text-gray-500">
                        {searchImported ? 'No customers match your search.' : 'No customers imported yet.'}
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {importedCustomers.map((customer) => (
                          <div
                            key={customer.externalId}
                            className="flex items-center p-3"
                          >
                            <div className="flex-shrink-0">
                              <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="ml-3 min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-900 truncate">{customer.name}</p>
                              {customer.domain && (
                                <p className="text-xs text-gray-500 truncate">{customer.domain}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  {selectedImports.length > 0
                    ? `${selectedImports.length} customer(s) selected for import`
                    : 'Select customers from the left panel to import'}
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowImportModal(false)}
                    className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleImport}
                    disabled={selectedImports.length === 0 || importing}
                    className="rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {importing ? 'Importing...' : `Import ${selectedImports.length} Customer(s)`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Customer Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowAddModal(false)} />
            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Customer</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name *</label>
                  <input
                    type="text"
                    value={newCustomer.name}
                    onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                    className="mt-1 block w-full rounded-md border border-gray-300 bg-white text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-2.5 px-3 text-base"
                    placeholder="Customer name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Domain</label>
                  <input
                    type="text"
                    value={newCustomer.domain}
                    onChange={(e) => setNewCustomer({ ...newCustomer, domain: e.target.value })}
                    className="mt-1 block w-full rounded-md border border-gray-300 bg-white text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-2.5 px-3 text-base"
                    placeholder="example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <select
                    value={newCustomer.status}
                    onChange={(e) => setNewCustomer({ ...newCustomer, status: e.target.value })}
                    className="mt-1 block w-full rounded-md border border-gray-300 bg-white text-gray-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 py-2.5 px-3 text-base"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Prospect">Prospect</option>
                  </select>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse gap-2">
                <button
                  onClick={handleCreateCustomer}
                  disabled={!newCustomer.name.trim() || creating}
                  className="inline-flex w-full justify-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 disabled:opacity-50 sm:w-auto"
                >
                  {creating ? 'Creating...' : 'Create Customer'}
                </button>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
